import React, { useState, useMemo, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { Alert, AlertTitle, Box, LinearProgress, linearProgressClasses, Snackbar, useTheme } from '@mui/material';

/**
 * ERA² SnackBar Component
 *
 * A sophisticated notification system built on MUI's Snackbar and Alert components,
 * featuring an integrated progress bar, configurable positioning, delayed appearance,
 * and comprehensive theming support. Designed for rich user feedback in modern
 * web applications with both visual and programmatic control over notification lifecycle.
 *
 * Key Features:
 * - Visual progress indication with animated progress bar
 * - Configurable initial delay for staggered notifications
 * - Full positioning control (9 possible positions)
 * - Four semantic alert types with theme integration
 * - Auto-dismiss with manual close override capability
 * - Accessible design with proper ARIA attributes
 * - Rich content support including HTML and React elements
 * - Performance optimized with memoization and cleanup
 * - Responsive design that adapts to screen sizes
 * - Theme-aware styling with color contrast compliance
 *
 * Architecture:
 * - Built on MUI Snackbar for accessibility and browser compatibility
 * - Uses MUI Alert for semantic styling and icon integration
 * - Custom progress tracking with precise timing control
 * - State management for visibility, progress, and lifecycle
 * - Event handling for user interactions and auto-dismiss
 * - Theme integration for consistent visual design
 *
 * Notification Types:
 * - Error: Critical failures, validation errors, system issues
 * - Warning: Important notices, potential problems, confirmations needed
 * - Info: General information, status updates, tips
 * - Success: Completed actions, positive confirmations, achievements
 *
 * Common Use Cases:
 * - Form submission feedback (success/error notifications)
 * - API operation status (loading, success, failure states)
 * - User action confirmations (save, delete, update operations)
 * - System status updates (connectivity, sync status)
 * - Onboarding and help messages for new users
 * - Real-time notifications (messages, alerts, reminders)
 * - Bulk operation progress (batch uploads, mass updates)
 * - Feature announcements and product updates
 *
 * Progress Bar Integration:
 * - Visual countdown showing time remaining until auto-dismiss
 * - Smooth animation with configurable update intervals
 * - Pause-on-hover functionality (inherited from Snackbar)
 * - Manual close overrides automatic progress
 * - High contrast progress indication for accessibility
 *
 * Positioning System:
 * - 6 possible positions using vertical (top/bottom) + horizontal (left/center/right)
 * - Smart positioning avoids overlap with navigation and UI elements
 * - Mobile-responsive positioning with safe area considerations
 * - Z-index management for proper layering
 *
 * @param {Object} props - Component props
 * @returns {JSX.Element} Rendered snackbar notification with progress tracking
 */
const SnackBar = ({
  title = 'SnackBar',
  type = 'info',
  vertical = 'top',
  horizontal = 'right',
  duration = 15000,
  children,
  open = true,
  onClose = () => { },
  initialDelay = 0,
}) => {
  // Component state management
  const [progress, setProgress] = useState(0); // Progress percentage (0-100)
  const [isVisible, setIsVisible] = useState(false); // Visibility state separate from open prop

  // Theme integration for consistent styling
  const theme = useTheme();

  // Progress calculation constants
  const intervalTime = 100; // Update interval in milliseconds (10fps for smooth animation)

  /**
   * Calculate progress step for smooth animation
   *
   * Formula: 100% / (total duration / update interval)
   * Example: For 15000ms duration with 100ms intervals:
   * - Total intervals: 15000 / 100 = 150 intervals
   * - Progress per interval: 100 / 150 = 0.667% per step
   *
   * This ensures the progress bar completes exactly when duration expires.
   */
  const progressStep = useMemo(() => 100 / (duration / intervalTime), [duration]);

  /**
   * Handle snackbar close events
   *
   * Supports both user-initiated closes (X button) and programmatic closes.
   * Filters out 'clickaway' events to prevent accidental dismissal.
   * Resets progress state for clean next usage.
   *
   * @param {Event} event - Close event object
   * @param {string} reason - Reason for closing ('clickaway', 'escapeKeyDown', 'timeout')
   */
  const handleClose = useCallback(
    (event, reason) => {
      // Ignore clickaway events to prevent accidental dismissal
      // Users should explicitly close notifications or wait for timeout
      if (reason === 'clickaway') {
        return;
      }

      // Clean state transition
      setIsVisible(false); // Hide immediately
      onClose(event, reason); // Notify parent component
      setProgress(0); // Reset progress for next usage
    },
    [onClose],
  );

  /**
   * Handle initial delay and visibility control
   *
   * Manages the delayed appearance feature which allows for:
   * - Staggered notifications in sequences
   * - Delayed feedback for async operations
   * - Better UX for rapid-fire notifications
   *
   * Uses setTimeout for precise timing control with proper cleanup.
   */
  useEffect(() => {
    if (open) {
      // Set up delayed appearance
      const delayTimer = setTimeout(() => {
        setIsVisible(true); // Show the snackbar
        setProgress(0); // Reset progress counter
      }, initialDelay);

      // Cleanup: Cancel timer if component unmounts or open changes
      return () => clearTimeout(delayTimer);
    } else {
      // If open becomes false, hide immediately
      setIsVisible(false);
    }
  }, [open, initialDelay]);

  /**
   * Handle progress bar animation and auto-dismiss
   *
   * Creates a smooth progress animation that provides visual feedback
   * about remaining time. Uses setInterval for consistent timing.
   *
   * Progress Logic:
   * 1. Start progress when snackbar becomes visible
   * 2. Update progress every intervalTime milliseconds
   * 3. Increment by progressStep each update
   * 4. Auto-close when progress reaches 100%
   * 5. Clean up interval on component unmount or visibility change
   */
  useEffect(() => {
    let progressTimer;

    if (isVisible) {
      progressTimer = setInterval(() => {
        setProgress((prevProgress) => {
          // Check if progress is complete
          if (prevProgress >= 100) {
            clearInterval(progressTimer); // Stop the timer
            handleClose(null, 'timeout'); // Auto-close with timeout reason
            return 0; // Reset progress
          }

          // Increment progress, ensuring it doesn't exceed 100%
          return Math.min(prevProgress + progressStep, 100);
        });
      }, intervalTime);

      // Cleanup function: Clear interval when effect dependencies change
      return () => clearInterval(progressTimer);
    }

    // Return empty cleanup function when not visible
    return () => { };
  }, [isVisible, progressStep, intervalTime, handleClose]);

  return (
    <Snackbar
      open={isVisible} // Use internal visibility state for better control
      anchorOrigin={{ vertical, horizontal }} // Position configuration
      autoHideDuration={duration} // Fallback auto-hide (progress bar handles primary timing)
      onClose={handleClose} // Close event handler
      // Additional props for better UX
      disableWindowBlurListener={false} // Keep notifications visible when window loses focus
    >
      <Alert
        onClose={handleClose} // Enable X button for manual close
        severity={type} // Maps to MUI alert types with appropriate icons/colors
        variant="filled" // Filled variant for better visibility and contrast
        sx={{
          width: '100%', // Full width within snackbar container
          p: 2, // Generous padding for comfortable reading

          // Theme-aware text color for optimal contrast
          color: theme.palette[type]?.contrastText || theme.palette.text.primary,

          // Typography styling for content elements
          '& span': {
            fontSize: '1rem',
            fontWeight: '400', // Normal weight for body text
          },

          // Link styling within notifications
          '& a': {
            color: 'inherit', // Inherit alert text color
            textDecoration: 'none', // Clean appearance
            '&:hover': {
              textDecoration: 'underline', // Hover feedback
            },
          },
        }}
      >
        {/* 
          Alert Title
          Provides clear hierarchy and scannable content structure
        */}
        <AlertTitle sx={{ fontWeight: '600', mb: 1 }}>{title}</AlertTitle>

        {/* 
          Alert Content
          Supports rich content including text, HTML, and React elements
        */}
        <span>{children}</span>

        {/* 
          Progress Bar Container
          Shows visual countdown until auto-dismiss
        */}
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate" // Shows specific progress value
            value={progress} // Current progress percentage (0-100)
            sx={{
              height: 4, // Slightly thicker for better visibility
              borderRadius: 2, // Rounded corners for modern appearance
              backgroundColor: 'rgba(255, 255, 255, 0.3)', // Semi-transparent background

              // Progress bar styling
              [`& .${linearProgressClasses.bar}`]: {
                // High contrast color for accessibility
                backgroundColor: theme.palette.common.black,
                borderRadius: 2, // Rounded corners to match container
                transition: 'transform 0.1s linear', // Smooth progress animation
              },
            }}
            // Accessibility attributes
            role="progressbar"
            aria-label={`Notification will close in ${Math.ceil(
              ((100 - progress) * duration) / 10000,
            )} seconds`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          />
        </Box>
      </Alert>
    </Snackbar>
  );
};

/*
PROP DOCUMENTATION:
==================

title - String (Optional):
  Title displayed at the top of the notification in bold text.
  Provides clear content hierarchy and scannable structure.
  
  Default: "SnackBar"
  
  Guidelines:
  - Keep concise (1-5 words) for quick scanning
  - Use title case for consistency
  - Make it descriptive of the notification type
  - Consider internationalization for multi-language apps
  
  Examples:
  - "Success": For positive confirmations
  - "Error": For failure notifications  
  - "Warning": For important notices
  - "Upload Complete": For specific action feedback
  - "Connection Lost": For system status updates

type - String (Optional):
  Semantic type that determines icon, color scheme, and urgency level.
  Maps to MUI Alert severity levels with theme integration.
  
  Options: 'error' | 'warning' | 'info' | 'success'
  Default: 'info'
  
  Type Guidelines:
  - 'error': Critical failures, validation errors, system problems
  - 'warning': Important notices, potential issues, confirmations needed
  - 'info': General information, status updates, helpful tips
  - 'success': Completed actions, positive confirmations, achievements
  
  Visual Characteristics:
  - Each type has distinct color scheme and icon
  - Text contrast automatically calculated for accessibility
  - Icons provide immediate visual context
  - Colors follow WCAG contrast guidelines

vertical - String (Optional):
  Vertical positioning of the notification on screen.
  
  Options: 'top' | 'bottom'
  Default: 'top'
  
  Positioning Strategy:
  - 'top': Less intrusive, doesn't block action areas
  - 'bottom': More prominent, good for critical notifications
  
  Considerations:
  - Mobile keyboards may hide bottom notifications
  - Top position works better with sticky headers
  - Consider navigation bar placement when choosing position

horizontal - String (Optional):
  Horizontal positioning of the notification on screen.
  
  Options: 'left' | 'center' | 'right'
  Default: 'right'
  
  Positioning Guidelines:
  - 'right': Standard for most applications, non-intrusive
  - 'center': Prominent, good for critical system messages
  - 'left': Alternative for right-heavy UI layouts
  
  UX Considerations:
  - Right position avoids interfering with navigation
  - Center position demands immediate attention
  - Consider reading direction (RTL languages) when choosing

duration - Number (Optional):
  Auto-dismiss duration in milliseconds.
  Controls both progress bar animation and automatic closure.
  
  Default: 15000 (15 seconds)
  
  Duration Guidelines:
  - Short (3-5s): Simple confirmations, success messages
  - Medium (8-12s): Information that needs reading time
  - Long (15-20s): Complex content, important warnings
  - Very Long (30s+): Critical information requiring action
  
  Accessibility Considerations:
  - Minimum 3 seconds for users to notice and read
  - Consider reading speed and content complexity
  - Provide manual close option for all durations
  - Test with screen readers for appropriate timing

children - Node (Required):
  Content to display in the notification body.
  Supports text, HTML elements, and React components.
  
  Content Guidelines:
  - Keep concise but informative
  - Use clear, actionable language
  - Include relevant context for the user
  - Consider including links for additional actions
  
  Formatting Support:
  - Plain text for simple messages
  - HTML for basic formatting (bold, italic, links)
  - React elements for complex content (buttons, forms)
  - Accessibility: Ensure content is screen reader friendly

open - Boolean (Optional):
  Controls whether the notification should be displayed.
  Triggers the entire notification lifecycle when changed.
  
  Default: true
  
  Usage Patterns:
  - Set to true when notification should appear
  - Set to false to programmatically dismiss
  - Use state variable for dynamic control
  - Combine with initialDelay for sequences

onClose - Function (Optional):
  Callback function executed when notification closes.
  Receives event and reason parameters for detailed handling.
  
  Default: () => {}
  
  Callback Parameters:
  - event: Close event object (may be null for programmatic closes)
  - reason: String indicating close reason ('clickaway', 'escapeKeyDown', 'timeout')
  
  Common Usage:
  - Update parent component state
  - Log notification interactions
  - Chain multiple notifications
  - Clean up associated timers or listeners

initialDelay - Number (Optional):
  Delay in milliseconds before showing the notification.
  Useful for creating notification sequences or delayed feedback.
  
  Default: 0 (show immediately)
  
  Use Cases:
  - Stagger multiple notifications to avoid overlap
  - Delay feedback until async operations complete
  - Create dramatic timing for important announcements
  - Sequence onboarding messages with proper pacing
  
  Performance Notes:
  - Uses setTimeout with proper cleanup
  - Cancels delay if component unmounts
  - Minimal performance impact for reasonable delays

ACCESSIBILITY FEATURES:
======================

- ARIA attributes on progress bar with descriptive labels
- Proper color contrast ratios for all notification types
- Semantic HTML structure with appropriate roles
- Keyboard navigation support (Escape to close)
- Screen reader announcements for state changes
- Focus management for interactive elements
- High contrast mode compatibility
- Reduced motion respect for progress animations

PERFORMANCE CONSIDERATIONS:
==========================

- Memoized component prevents unnecessary re-renders
- Efficient interval management with proper cleanup
- Progress calculation optimized with useMemo
- Event handlers memoized with useCallback
- Minimal DOM updates with React state batching
- Theme access optimized to prevent excessive lookups

THEMING INTEGRATION:
===================

- Automatic color scheme adaptation for light/dark themes
- Proper contrast text calculation for accessibility
- Integration with MUI theme palette for consistency
- Custom styling respects theme breakpoints
- Typography scales follow theme configuration
- Spacing follows theme spacing scale
*/

// Comprehensive PropTypes for development-time validation
SnackBar.propTypes = {
  /** Title displayed at the top of the snackbar. Should be concise and descriptive */
  title: PropTypes.string,

  /** Semantic type determining color scheme and icon. Maps to alert severity levels */
  type: PropTypes.oneOf(['error', 'warning', 'info', 'success']),

  /** Vertical position on screen. Consider mobile keyboards and navigation placement */
  vertical: PropTypes.oneOf(['top', 'bottom']),

  /** Horizontal position on screen. Consider UI layout and reading direction */
  horizontal: PropTypes.oneOf(['left', 'center', 'right']),

  /** Auto-dismiss duration in milliseconds. Consider reading time and content complexity */
  duration: PropTypes.number,

  /** Content to display in notification body. Supports text, HTML, and React elements */
  children: PropTypes.node.isRequired,

  /** Controls notification visibility. Triggers lifecycle when changed */
  open: PropTypes.bool,

  /** Callback executed when notification closes. Receives event and reason parameters */
  onClose: PropTypes.func,

  /** Initial delay before showing notification. Useful for sequences and timing control */
  initialDelay: PropTypes.number,
};

// Memoization prevents unnecessary re-renders when parent components update
// but notification props remain the same, improving performance especially
// with multiple notifications or frequently updating parent state
export default memo(SnackBar);

// Display name for React DevTools debugging and component identification
SnackBar.displayName = 'ERA² SnackBar';
